import React from 'react'
import { createComponent } from '@ycos/fela'
import { getFontStyles } from '@ycos/primitives'
import { Analytics } from '@ycos/analytics'
import { trackCreateAccountButtonClick, trackSignInButtonClick } from './analytics/analytics'

const LOGIN_BASE_URL = '/account/login'
const SIGNUP_BASE_URL = '/account/register'

const Wrapper = createComponent('Wrapper', () => {
  return {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: 0
  }
})

const MessageBlock = createComponent(
  'MessageBlock',
  ({ theme, locale, brand }) => {
    const fontName = brand === 'theoutnet' ? '4' : '3'
    return {
      ...getFontStyles(theme.typography, { type: 'Paragraph', tagName: 'p', name: fontName }),
      ...(locale.language === 'ar' && {
        fontSize: brand === 'mrp' ? '22px' : '16px',
        lineHeight: brand === 'mrp' ? '33Px' : '24px'
      }),
      color: theme?.typography?.colors?.PrimaryBlack,
      fontWeight: '400',
      textAlign: 'center',
      margin: '0 auto',
      'screen-medium': {
        padding: `0 ${2 * theme.spacingMultiplier}px`
      }
    }
  },
  'p'
)

const ButtonWrapper = createComponent('ButtonWrapper', ({ theme }) => {
  return {
    marginTop: `${3 * theme.spacingMultiplier}px`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    'screen-medium': {
      flexDirection: 'row'
    }
  }
})

const SignUpButton = createComponent(
  'SignUpButton',
  ({ theme, locale }) => {
    return {
      ...getFontStyles(theme.typography, { type: 'Label', tagName: 'p', name: '1' }),
      fontWeight: '400',
      width: '100%',
      height: '48px',
      backgroundColor: locale.language === 'ar' ? theme?.typography?.colors?.PrimaryBlack : theme?.typography?.colors?.PrimaryWhite,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      color: locale.language === 'ar' ? theme?.typography?.colors?.PrimaryWhite : theme?.typography?.colors?.PrimaryBlack,
      margin: '0',
      textDecoration: 'none',
      border: `1px solid ${theme?.typography?.colors?.PrimaryBlack}`,
      'screen-medium': {
        marginRight: `${theme.spacingMultiplier}px`,
        height: '40px'
      }
    }
  },
  'a'
)

const SignInButton = createComponent(
  'SignInButton',
  ({ theme, locale }) => {
    return {
      ...getFontStyles(theme.typography, { type: 'Label', tagName: 'p', name: '1' }),
      fontWeight: '400',
      width: '100%',
      height: '48px',
      color: locale.language === 'ar' ? theme?.typography?.colors?.PrimaryBlack : theme?.typography?.colors?.PrimaryWhite,
      backgroundColor: locale.language === 'ar' ? theme?.typography?.colors?.PrimaryWhite : theme?.typography?.colors?.PrimaryBlack,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      border: `1px solid ${theme?.typography?.colors?.PrimaryBlack}`,
      marginTop: `${theme.spacingMultiplier}px`,
      textDecoration: 'none',
      'screen-medium': {
        marginTop: '0',
        height: '40px'
      }
    }
  },
  'a'
)

const Title = createComponent(
  'Title',
  ({ theme, brand, locale }) => {
    const arFontWeight = brand === 'nap' ? '700 !important' : '400 !important'
    const noArFontWeight = brand === 'nap' || 'theoutnet' ? '400 !important' : '850 !important'
    return {
      ...getFontStyles(theme.typography, brand === 'nap' ? { type: 'Heading', name: 'B2' } : { type: 'Heading', name: 'A5' }),
      ...(locale.language === 'ar' && {
        fontSize: brand === 'nap' ? '26px' : '24px',
        lineHeight: brand === 'nap' ? '32.5px' : '30px'
      }),
      color: theme.typography.colors.PrimaryBlack,
      textAlign: 'center',
      margin: '0 0 8px',
      fontWeight: locale.language === 'ar' ? arFontWeight : noArFontWeight,
      ...(brand === 'mrp' ? { fontSize: '20px' } : {}),
      'screen-medium': {
        fontSize: brand === 'nap' ? '32px' : '24px',
        ...(locale.language === 'ar' && {
          fontSize: brand === 'nap' ? '34px' : '28px',
          lineHeight: brand === 'nap' ? '42.5px' : '35Px'
        })
      },
      'screen-large': {
        ...(locale.language === 'ar' && {
          fontSize: brand === 'nap' ? '34px' : '28px',
          lineHeight: brand === 'nap' ? '42.5px' : '35Px'
        })
      }
    }
  },
  'h2'
)

const LogInComponent = ({ item, locale, brand }) => {
  const { teaserText, preTitle, teaserTitle, subTitle } = item
  const { id = '' } = locale
  const loginUrl = `/${id}${LOGIN_BASE_URL}`
  const signUpUrl = `/${id}${SIGNUP_BASE_URL}`

  const onCreateAccountButtonClick = () => {
    const analytics = new Analytics({ brand: brand.brandId })
    analytics.trackEvent(trackCreateAccountButtonClick())
  }

  const onSignInButtonClick = () => {
    const analytics = new Analytics({ brand: brand.brandId })
    analytics.trackEvent(trackSignInButtonClick())
  }

  return (
    <Wrapper data-testid="login-component">
      <Title locale={locale} brand={brand}>
        {teaserTitle}
      </Title>
      <MessageBlock locale={locale} brand={brand}>
        {teaserText}
      </MessageBlock>
      <ButtonWrapper>
        <SignUpButton locale={locale} href={signUpUrl} onClick={onCreateAccountButtonClick}>
          {preTitle}
        </SignUpButton>
        <SignInButton locale={locale} href={loginUrl} onClick={onSignInButtonClick}>
          {subTitle}
        </SignInButton>
      </ButtonWrapper>
    </Wrapper>
  )
}

export default LogInComponent
